import React, { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { api } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { useSiteImages } from "../context/SiteImagesContext";
import { Avatar } from "../components/Avatar";
import { StyleTag, MetaChip, Loader, Card } from "../components/ui-bits";
import { Music2, Send, Inbox } from "lucide-react";

export default function PublicSearch() {
  const { code } = useParams();
  const nav = useNavigate();
  const { user } = useAuth();
  const { img } = useSiteImages();
  const [s, setS] = useState(null);
  const [nf, setNf] = useState(false);

  useEffect(() => {
    api.get(`/searches/public/${code}`).then((r) => setS(r.data)).catch(() => setNf(true));
  }, [code]);

  const propose = () => {
    if (!user) { nav(`/register?role=beatmaker`); return; }
    if (user.role === "beatmaker") nav(`/app/search/${s.id}`);
    else nav("/app");
  };

  if (nf) return (
    <div className="min-h-screen flex flex-col items-center justify-center gap-3 px-4">
      <Inbox size={40} className="text-zinc-600" />
      <p className="text-zinc-400">Cette recherche est introuvable.</p>
      <Link to="/" className="text-violet-400 font-medium">Retour à l'accueil</Link>
    </div>
  );
  if (!s) return <div className="min-h-screen flex items-center justify-center"><Loader /></div>;

  return (
    <div className="min-h-screen">
      <header className="glass border-b border-zinc-800/70 sticky top-0 z-20">
        <div className="max-w-2xl mx-auto px-4 h-14 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2">
            <img src={img("logo")} alt="ilovemuzik" className="w-8 h-8 rounded-lg object-contain bg-black" />
            <span className="font-display font-extrabold">ILOVE<span className="gradient-text">MUZIK</span></span>
          </Link>
          {!user && <Link to="/login" className="text-sm text-zinc-300 hover:text-white">Connexion</Link>}
        </div>
      </header>

      <div className="max-w-2xl mx-auto px-4 py-8">
        <div className="absolute top-14 left-1/2 -translate-x-1/2 w-[400px] h-[300px] bg-violet-600/15 rounded-full blur-3xl -z-10" />
        <p className="text-xs uppercase tracking-widest text-violet-400 font-mono mb-3">Recherche de production</p>
        <Card className="p-6 animate-fade-up">
          <div className="flex items-center gap-3 mb-5">
            <Avatar user={s.artist} size={52} />
            <div>
              <p className="font-display font-bold text-lg">🎤 @{s.artist?.pseudo}</p>
              {s.artist?.artist_name && <p className="text-sm text-zinc-400">{s.artist.artist_name}</p>}
            </div>
          </div>
          <h1 className="text-2xl font-display font-bold mb-4">{s.title}</h1>
          <div className="flex flex-wrap gap-2 mb-5">{(s.styles || []).map((x) => <StyleTag key={x}>{x}</StyleTag>)}</div>
          <div className="grid grid-cols-3 gap-4 mb-5">
            <MetaChip label="BPM" value={s.bpm_min ? `${s.bpm_min}${s.bpm_max ? `–${s.bpm_max}` : ""}` : "—"} />
            <MetaChip label="Tonalité" value={s.key} />
            <MetaChip label="Budget" value={s.budget} />
          </div>
          {s.moods?.length > 0 && (
            <div className="mb-4">
              <p className="text-[10px] uppercase tracking-widest text-zinc-500 font-mono mb-1.5">Ambiance</p>
              <div className="flex flex-wrap gap-2">{s.moods.map((m) => <span key={m} className="text-xs px-2 py-0.5 rounded-md bg-zinc-800 text-zinc-300">{m}</span>)}</div>
            </div>
          )}
          {s.references && <p className="text-sm text-zinc-400 mb-2"><span className="text-zinc-500">Références :</span> {s.references}</p>}
          {s.description && <p className="text-sm text-zinc-300 leading-relaxed mb-2">{s.description}</p>}
          <p className="text-xs text-zinc-500 font-mono mt-4">{s.submission_count || 0} proposition(s) reçue(s)</p>
        </Card>

        <button onClick={propose} data-testid="public-propose-button"
          className="w-full mt-5 flex items-center justify-center gap-2 py-4 rounded-xl bg-gradient-to-r from-violet-600 to-purple-600 hover:from-violet-500 hover:to-purple-500 font-semibold text-white glow-violet transition-all">
          <Send size={18} /> PROPOSER MES PRODUCTIONS
        </button>
        {!user && <p className="text-center text-xs text-zinc-500 mt-3">Connecte-toi ou crée un compte beatmaker pour proposer tes prods.</p>}
      </div>
    </div>
  );
}
